import {
  ChakraBox,
  ChakraFlex,
  EmptySectionCard,
  FAIconName,
  SkeletonLoader,
  Text,
  ToastMessage,
} from '@equip.health/ui';
import { useEffect, useState } from 'react';

import { StatusCodes } from '~/lib/constants';
import {
  MEDICATIONS_UNAVAILABLE_MESSAGE,
  PATIENT_MEDICATIONS_TAB_HEADING,
  PatientUniversalStatusCode,
  SCRIPTSURE_PATIENT_NOT_FOUND_MESSAGE,
  SCRIPTSURE_PRESCRIPTION_LOAD_ERROR_MESSAGE,
  SCRIPTSURE_PROVIDER_NOT_REGISTERED_MESSAGE,
} from '~/lib/constants/patients.constants';
import { isBaseQueryError } from '~/lib/types/api/isQueryError';
import { useGetScriptSurePrescribingURLQuery } from '~/store/services/integration';

const TOP_LEVEL_ID = 'patient-medications';

interface PatientMedicationsTabProps {
  patient: Patient;
}

const PatientMedicationsTab = ({ patient }: PatientMedicationsTabProps) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false);

  const { userId, universalStatusCode } = patient;

  const canViewMedications =
    universalStatusCode === PatientUniversalStatusCode.IN_TREATMENT ||
    universalStatusCode === PatientUniversalStatusCode.DISCHARGED;

  const {
    currentData: prescribingUrl,
    error,
    isFetching,
  } = useGetScriptSurePrescribingURLQuery(userId, {
    skip: !canViewMedications || !userId,
  });

  const status = isBaseQueryError(error) ? error.status : undefined;

  const isProviderNotRegistered = status === StatusCodes.CONFLICT;

  const isPatientNotFound = status === StatusCodes.NOT_FOUND;

  const isWarning = isProviderNotRegistered || isPatientNotFound;

  let errorMessage = SCRIPTSURE_PRESCRIPTION_LOAD_ERROR_MESSAGE;
  if (isProviderNotRegistered) {
    errorMessage = SCRIPTSURE_PROVIDER_NOT_REGISTERED_MESSAGE;
  } else if (isPatientNotFound) {
    errorMessage = SCRIPTSURE_PATIENT_NOT_FOUND_MESSAGE;
  }

  useEffect(() => {
    setIsIframeLoaded(false);
  }, [prescribingUrl?.url]);

  const handleIframeLoad = () => setIsIframeLoaded(true);

  return (
    <SkeletonLoader
      id={`${TOP_LEVEL_ID}__skeleton-loader`}
      height="100%"
      isLoaded={isIframeLoaded || !isFetching}
    >
      {error && (
        <ChakraBox mt="12px">
          <ToastMessage
            id={`${TOP_LEVEL_ID}__prescribing-url-error_toast`}
            icon={FAIconName.exclamationTriangleRegular}
            isFullWidth
            text={errorMessage}
            variant={isWarning ? 'warning' : 'critical'}
          />
        </ChakraBox>
      )}

      {prescribingUrl?.url && (
        <ChakraBox h="100%" mt="10px">
          <iframe
            allowFullScreen
            data-testid="patient-medications"
            src={prescribingUrl.url}
            title="patient-medications"
            onLoad={handleIframeLoad}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        </ChakraBox>
      )}

      {!error && !prescribingUrl?.url && (
        <ChakraBox display="flex" flexDirection="column" gap="10px">
          <ChakraFlex
            alignItems="center"
            justifyContent="space-between"
            mt="12px"
          >
            <Text variant="h5">{PATIENT_MEDICATIONS_TAB_HEADING}</Text>
          </ChakraFlex>

          {!canViewMedications && (
            <EmptySectionCard
              id={`${TOP_LEVEL_ID}__medications-empty-card`}
              caption={MEDICATIONS_UNAVAILABLE_MESSAGE}
            />
          )}
        </ChakraBox>
      )}
    </SkeletonLoader>
  );
};

export default PatientMedicationsTab;
